import { useState, useEffect, useCallback } from 'react'

import { useApolloClient } from '@apollo/client'
import * as Sentry from '@sentry/nextjs'
import { IconQrcode, IconCamera, IconCheck } from '@tabler/icons-react'
import { message, Table } from 'antd'
import Link from 'next/link'
import { OnResultFunction, QrReader } from 'react-qr-reader'
import useSound from 'use-sound'

import { mapQrCodeData } from './qrCodeUtils'
import { QrContainer, SaveBanner } from './StyledQrCodeComponents'
import { ViewFinder } from './ViewFinder'

import { Alert } from '@/components/shared-components/BUI/Alert'
import { Button } from '@/components/shared-components/BUI/Button'
import { FullscreenModal } from '@/components/shared-components/BUI/Modal'
import { Text } from '@/components/shared-components/BUI/Text'
import { Flexbox } from '@/components/shared-components/Flexbox'
import Loading from '@/components/shared-components/LoadingPage'
import { useLocale } from '@/context/localeContext'
import { TimezonedDate } from '@/context/timezoneContext'
import { GET_SITE_INDUCTION_BY_IDENTIFIER_PUBLIC } from '@/modules/inductions/graphQL'
import { InductionTag } from '@/modules/inductions/InductionTag'
import { WorkerQrCodeSignatureItem } from '@/modules/templates/types/blueprint'
import scanSound from '@/public/sounds/scan.mp3'
import { pluralise } from '@/utils/pluralise'
import { routes } from '@/utils/routes'

import {
  InductionStatus,
  SiteInductionByIdentifierPublicUiQuery,
  SiteInductionByIdentifierPublicUiQueryVariables,
} from 'types/api.graphql'

const ScanWorkerQrCodes = ({
  onScanListChange,
  scannedCodes,
}: {
  onScanListChange: (data: WorkerQrCodeSignatureItem[]) => void
  scannedCodes: WorkerQrCodeSignatureItem[]
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [cameraPermission, setCameraPermission] = useState<
    'granted' | 'denied' | 'pending'
  >('pending')
  const { t } = useLocale()
  const [isLoadingWorker, setIsLoadingWorker] = useState(false)
  const client = useApolloClient()
  const [lastResult, setLastResult] = useState(null)
  const [lastScannedValue, setLastScannedValue] = useState<
    | (WorkerQrCodeSignatureItem & {
        error: false
        signedOnAt: Date
        status: InductionStatus
      })
    | { error: true }
    | null
  >()
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null)
  const [playScanSound] = useSound(scanSound, { volume: 0.5 })
  const [lastDuplicateWarningTime, setLastDuplicateWarningTime] =
    useState<number>(0)

  useEffect(() => {
    // Reset scanning state when modal opens
    if (isModalOpen && cameraPermission === 'pending') {
      requestCameraPermission()
    } else if (!isModalOpen) {
      // Stop the camera when modal closes
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop())
        setCameraStream(null)
      }
    }

    // Cleanup function to ensure camera is stopped when component unmounts
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop())
      }
    }
  }, [isModalOpen, cameraPermission, cameraStream])

  useEffect(() => {
    if (!lastScannedValue || lastScannedValue?.error === true) {
      return
    }

    const isDuplicate = scannedCodes.some(
      (code) => code.workerId === lastScannedValue.workerId
    )

    if (isDuplicate) {
      const currentTime = Date.now()
      if (currentTime - lastDuplicateWarningTime > 2500) {
        message.warn('This worker has already been scanned')
        setLastDuplicateWarningTime(currentTime)
      }
      return
    }

    const scan: WorkerQrCodeSignatureItem = {
      workerId: lastScannedValue.workerId,
      firstName: lastScannedValue.firstName,
      lastName: lastScannedValue.lastName,
      signedViaQrCode: lastScannedValue.signedViaQrCode,
      createdAt: lastScannedValue.createdAt,
      signatureUrl: lastScannedValue.signatureUrl,
    }

    onScanListChange([...scannedCodes, scan])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastScannedValue])

  const requestCameraPermission = async () => {
    try {
      // Create a promise that rejects after 5 seconds
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(
          () => reject(new Error('Camera permission request timed out')),
          5000
        )
      })

      // Race between the camera request and the timeout
      const stream = (await Promise.race([
        navigator.mediaDevices.getUserMedia({ video: true }),
        timeoutPromise,
      ])) as MediaStream

      setCameraPermission('granted')
      setCameraStream(stream)

      // Clean up the stream since QrReader will create its own
      stream.getTracks().forEach((track) => track.stop())
    } catch (error) {
      console.error('Camera permission denied or timed out:', error)
      setCameraPermission('denied')
      const errorMessage =
        error instanceof Error && error.message.includes('timed out')
          ? 'Camera access request timed out. Please try again.'
          : 'Camera access is required to scan QR codes. Please allow camera access.'
      message.error(errorMessage)
    }
  }

  const handleScan: OnResultFunction = useCallback(
    async (result) => {
      if (result && isModalOpen) {
        setLastScannedValue(null)

        const scannedValue = result?.getText()
        setLastResult(result)

        // Clear the lastResult after 2.5 second to reset the viewfinder
        setTimeout(() => {
          setLastResult(null)
        }, 2500)

        const qrCodeData = mapQrCodeData(scannedValue)

        if (!qrCodeData) {
          setLastScannedValue({ error: true })
          Sentry.captureMessage('Invalid QR code', {
            extra: {
              scannedValue,
            },
          })
          return
        }

        playScanSound()

        setIsLoadingWorker(true)

        try {
          const workerDetails = await client.query<
            SiteInductionByIdentifierPublicUiQuery,
            SiteInductionByIdentifierPublicUiQueryVariables
          >({
            query: GET_SITE_INDUCTION_BY_IDENTIFIER_PUBLIC,
            variables: {
              inductionIdentifier: parseInt(qrCodeData.inductionIdentifier),
              projectId: qrCodeData.projectId,
            },
            fetchPolicy: 'network-only',
          })
          setIsLoadingWorker(false)

          if (!workerDetails.data.projectInductionByIdentifier_public) {
            message.error("Couldn't find worker details")
            return
          }

          setLastScannedValue({
            workerId:
              workerDetails.data.projectInductionByIdentifier_public.workerId,
            firstName:
              workerDetails.data.projectInductionByIdentifier_public.firstName,
            lastName:
              workerDetails.data.projectInductionByIdentifier_public.lastName,
            signedViaQrCode: true,
            createdAt: new Date().toISOString(),
            signedOnAt: workerDetails.data.projectInductionByIdentifier_public
              .signedOnAt
              ? new Date(
                  workerDetails.data.projectInductionByIdentifier_public.signedOnAt
                )
              : null,
            status:
              workerDetails.data.projectInductionByIdentifier_public.status,
            error: false,
          })
        } catch (error) {
          setLastScannedValue({ error: true })
          Sentry.captureException(error, {
            extra: {
              scannedValue,
              qrCodeData,
            },
          })
          setIsLoadingWorker(false)
        }
      }
    },
    [isModalOpen, client, playScanSound, setLastScannedValue, setLastResult]
  )

  const openModal = () => {
    setIsModalOpen(true)
    setLastResult(null)
    setLastScannedValue(null)
    setCameraPermission('pending')
  }

  const closeModal = () => {
    onScanListChange(scannedCodes)
    setIsModalOpen(false)
  }

  return (
    <>
      <Button
        onClick={openModal}
        icon={<IconQrcode />}
        style={{ width: '100%' }}
      >
        Scan worker QR codes
      </Button>

      <FullscreenModal
        destroyOnClose={true}
        title={
          <Flexbox flexDirection="row" gap="1rem" alignItems="flex-start">
            <IconQrcode size={32} />
            Mark attendance by scanning worker QR codes
          </Flexbox>
        }
        open={isModalOpen}
        onCancel={closeModal}
        footer={[
          <Button variant="primary" key="close" onClick={closeModal}>
            I&apos;m finished ({pluralise(scannedCodes.length, 'scan')})
          </Button>,
        ]}
      >
        <QrContainer>
          {isModalOpen && cameraPermission === 'granted' && (
            <QrReader
              onResult={handleScan}
              constraints={{
                facingMode: 'environment',
                aspectRatio: 1,
              }}
              videoStyle={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                position: 'relative',
              }}
              videoContainerStyle={{
                paddingTop: '0',
                width: '100%',
                height: '100%',
              }}
              scanDelay={2500}
              ViewFinder={(props) => (
                <ViewFinder {...props} result={lastResult} />
              )}
            />
          )}
          {cameraPermission === 'denied' && (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <p>Camera access is required to scan QR codes.</p>
              <Button
                onClick={requestCameraPermission}
                style={{ marginTop: '1rem' }}
                icon={<IconCamera />}
              >
                Request camera permission
              </Button>
            </div>
          )}
          {cameraPermission === 'pending' && (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <Loading />
            </div>
          )}

          {isLoadingWorker && (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <Loading />
            </div>
          )}
          {lastScannedValue && lastScannedValue.error === false && (
            <Flexbox flexDirection="column" gap="1rem" alignItems="center">
              <Table
                pagination={false}
                columns={[
                  {
                    title: 'Name',
                    dataIndex: 'worker',
                    render: (_text, record) => {
                      return (
                        <Link
                          href={routes.workers.view(record.workerId)}
                          target="_blank"
                        >
                          <Text variant="body02">
                            {record.firstName} {record.lastName}
                          </Text>
                        </Link>
                      )
                    },
                  },
                  {
                    title: 'Time',
                    dataIndex: 'createdAt',
                    render: (_text, record) => (
                      <TimezonedDate
                        date={record.createdAt}
                        includeDate={false}
                        includeTime={true}
                        showTimezone={false}
                      />
                    ),
                  },
                  {
                    title: t('induction:title'),
                    dataIndex: 'status',
                    render: (status) => <InductionTag status={status} />,
                  },
                ]}
                dataSource={[lastScannedValue]}
              />
              {!lastScannedValue.signedOnAt && (
                <Alert
                  variant="info"
                  title="We have signed the worker on to BuildPass on your behalf."
                />
              )}
              <SaveBanner>
                <IconCheck />
                Automatically saved
              </SaveBanner>
            </Flexbox>
          )}

          {lastScannedValue?.error === true && (
            <Alert
              variant="danger"
              title="Unexpected error scanning worker QR code. Please reach out to BuildPass support if this issue persists."
            />
          )}
        </QrContainer>
      </FullscreenModal>
    </>
  )
}

export default ScanWorkerQrCodes
